import React, { Component, useState, useEffect, useRef, useLayoutEffect } from 'react'

import { AnimatePresence, motion, useMotionValue, useTransform, animate, useScroll, useMotionValueEvent, useAnimationControls, motionValue } from "framer-motion"

import { NavLink, Link } from "react-router-dom"

import Modal from 'react-modal';

import IconButton from '../../elements/IconButton'
import Dropdown from '../../elements/ReactSelectDropdown'
import TextInput from '../../elements/TextInput'
import Button from '../../elements/Button'

import { jsPDF } from 'jspdf';
import 'jspdf-autotable'; // Import the plugin for jsPDF

const Main = (props) => {

    const [requestedCount, setRequestedCount] = useState(0);
    const [completedCount, setCompletedCount] = useState(0);
    const [allSpiffeeDetails, setAllSpiffeeDetails] = useState([]);
    
    function formatMoney2(value) {
        if (typeof value === "string" && value.trim() !== "") value = Number(value);
        if (typeof value !== "number" || !Number.isFinite(value)) {
            throw new TypeError("formatMoney2: value must be a finite number (or numeric string).");
        }

        const sign = value < 0 ? "-" : "";
        const abs = Math.abs(value);

        // Round to cents in integer space (fixes 1.005 -> 1.01, etc.)
        // Add a tiny epsilon relative to magnitude to reduce binary float edge cases.
        const eps = Number.EPSILON * abs;
        const cents = Math.round((abs + eps) * 100);

        const intPart = Math.floor(cents / 100);
        const fracPart = cents % 100;

        const intWithCommas = String(intPart).replace(/\B(?=(\d{3})+(?!\d))/g, ",");

        return `${sign}${intWithCommas}.${String(fracPart).padStart(2, "0")}`;
    }

    function toMMDDYYYY(isoString) {
        if (!isoString) return "";

        const d = new Date(isoString);

        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        const yyyy = d.getFullYear();

        return `${mm}/${dd}/${yyyy}`;
    }

    const downloadCSV = async () => {
        setRequestedCount(0);
        setCompletedCount(0);
        setAllSpiffeeDetails([]);

        const myHeaders = new Headers();
        myHeaders.append("Accept", "application/json");
        myHeaders.append("Authorization", "Bearer " + localStorage.getItem("access_token"));
        myHeaders.append("Content-Type", "application/json");

        const requestOptions = {
            method: "POST",
            headers: myHeaders,
            redirect: "follow",
            body: JSON.stringify({}),
        };

        const tokenSafeRequest = (url) =>
            new Promise((resolve, reject) => {
            props.tokenSafeAPIRequest(url, requestOptions, "", (result, status) => {
                if (status >= 200 && status < 300) return resolve(result);
                reject({ status, result });
            });
            });

        const escapeCSV = (value) => {
            if (value === null || value === undefined) return "";
            const s = String(value);
            // Quote if it contains comma, quote, or newline
            if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
            return s;
        };

        const downloadTextFile = (filename, text) => {
            const blob = new Blob([text], { type: "text/csv;charset=utf-8;" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        };

        const buildCSV = (agents) => {
            const header = ["Agent", "Dealership", "Spiffee", "Admin", "Spiff Date", "Spiff Amount"];

            const rows = [];
            rows.push(header);

            agents.forEach((agent) => {
            let agentTotal = 0;

            agent.spiffs.forEach((spiff) => {
                const amount = Number(spiff?.amount) || 0;
                agentTotal += amount;

                rows.push([
                agent?.user_ID?.last_name ?? "",
                spiff?.dealership_ID?.name ?? "",
                spiff?.spiffee_ID?.name ?? "",
                spiff?.admin_ID?.name ?? "",
                toMMDDYYYY(spiff?.timestamp) ?? "",
                formatMoney2(amount),
                ]);
            });

            // TOTAL ROW (end of agent)
            rows.push(["", "", "", "", "TOTAL", formatMoney2(agentTotal)]);

            // EMPTY SPACER ROW
            rows.push(["", "", "", "", "", ""]);
            });

            // Optional "title" row like the PDF has (keeps format-ish)
            // If you don't want it, delete the next 2 lines.
            rows.unshift(["NON-WEEKLY 7 OUTSTANDING PRE-SPIFFS"]);
            rows.splice(1, 0, []); // blank row after title

            const csv = rows
            .map((row) => row.map(escapeCSV).join(","))
            .join("\n");

            downloadTextFile("NON-WEEKLY 7 OUTSTANDING PRE-SPIFFS.csv", csv);
        };

        const processData = (data) => {
            const allSpiffs = [];
            data.forEach((spiffee) => {
            allSpiffs.push(...(spiffee?.spiffs ?? []));
            });

            const map = new Map();

            for (const spiff of allSpiffs) {
            const userId = spiff?.user_ID?.id;
            if (!userId) continue;

            if (!map.has(userId)) {
                map.set(userId, {
                user_ID: spiff.user_ID,
                spiffs: [],
                });
            }

            map.get(userId).spiffs.push(spiff);
            }

            buildCSV(Array.from(map.values()));
        };

        try {
            const list = await tokenSafeRequest(
            "/metrics/spiffee/prespiff?limit=9999999&skip=0&fetch_links=true&sort_field=timestamp"
            );

            const spiffees = Array.isArray(list?.spiffees) ? list.spiffees : [];
            setRequestedCount(spiffees.length);

            const collected = [];

            for (const spiffee of spiffees) {
            const detail = await tokenSafeRequest(`/metrics/spiffee/prespiff/${spiffee.id}?limit=99999`);
            collected.push(detail);
            setCompletedCount((c) => c + 1);
            }

            setAllSpiffeeDetails(collected);
            processData(collected);
        } catch (err) {
            console.error("Failed fetching spiffees", err);
        }
    };

    const downloadPDF = async () => {
        setRequestedCount(0);
        setCompletedCount(0);
        setAllSpiffeeDetails([]);

        const myHeaders = new Headers();
        myHeaders.append("Accept", "application/json");
        myHeaders.append("Authorization","Bearer " + localStorage.getItem("access_token"));
        myHeaders.append("Content-Type", "application/json");

        const requestOptions = {
            method: "POST",
            headers: myHeaders,
            redirect: "follow",
            body: JSON.stringify({}),
        };

        const tokenSafeRequest = (url) =>
        new Promise((resolve, reject) => {
            props.tokenSafeAPIRequest(url, requestOptions, "", (result, status) => {
            if (status >= 200 && status < 300) return resolve(result);
                reject({ status, result });
            });
        });

        try {
        const list = await tokenSafeRequest(
            "/metrics/spiffee/prespiff?limit=9999999&skip=0&fetch_links=true&sort_field=timestamp"
        );

        const spiffees = Array.isArray(list?.spiffees) ? list.spiffees : [];
        setRequestedCount(spiffees.length);

        const collected = [];

        for (const spiffee of spiffees) {
            const detail = await tokenSafeRequest(
            `/metrics/spiffee/prespiff/${spiffee.id}?limit=99999`
            );

            collected.push(detail);
            setCompletedCount((c) => c + 1);
        }

        setAllSpiffeeDetails(collected);

        const buildPDF = (data) => {
            const doc = new jsPDF({ orientation: 'landscape' });
            doc.text('NON-WEEKLY 7 OUTSTANDING PRE-SPIFFS', 20, 10);

            let pageCount = 1;

            const addPageNumbers = (doc) => {
                const pageSize = doc.internal.pageSize;
                const pageHeight = pageSize.height;
                const pageWidth = pageSize.width;
        
                // Add footer with page number
                const pageCount = doc.internal.getNumberOfPages();
                for (let i = 1; i <= pageCount; i++) {
                    doc.setPage(i);
                    doc.text(
                        `Page ${i}`,
                        pageWidth - 30,
                        pageHeight - 10
                    );
                }
            };

            let tableBody = [];

            data.forEach(agent => {
            let agentTotal = 0;

            agent.spiffs.forEach(spiff => {
                const amount = Number(spiff?.amount) || 0;
                agentTotal += amount;

                tableBody.push([
                agent?.user_ID?.last_name ?? "",
                spiff?.dealership_ID?.name ?? "",
                spiff?.spiffee_ID?.name ?? "",
                spiff?.admin_ID?.name ?? "",
                toMMDDYYYY(spiff?.timestamp) ?? "",
                formatMoney2(amount)
                ]);
            });

            // TOTAL ROW (end of agent)
            tableBody.push([
                "", "", "", "", "TOTAL",
                formatMoney2(agentTotal)
            ]);

            // EMPTY SPACER ROW
            tableBody.push([
                "", "", "", "", "", ""
            ]);
            });

            console.log(tableBody)

            doc.autoTable({
                startY: 22,
                head: [['Agent','Dealership', 'Spiffee', 'Admin','Spiff Date','Spiff Amount']],
                body: tableBody,
                styles: {
                    lineColor: [200, 200, 200], // Light grey border lines
                    lineWidth: 0.1              // Thin border lines
                },
                bodyStyles: {
                    fillColor: [0, 0, 0],             // Disable row fill color completely
                },
                headStyles: {
                    fillColor: false,            // No background for header row
                    textColor: 0,                // Black text
                    lineColor: [200, 200, 200],  // Light grey border lines for header
                    lineWidth: 0.1
                },
                tableLineColor: [200, 200, 200], // Light grey border around table
                tableLineWidth: 0.1,
                willDrawCell: function(data) {
                    data.cell.styles.fillColor = false; // Override alternating row colors
                }
            });

            addPageNumbers(doc);

            doc.save('NON-WEEKLY 7 OUTSTANDING PRE-SPIFFS');
        }

        const processData = (data) => {
            let allSpiffs = []
            data.map((spiffee) => {
                allSpiffs.push(...spiffee?.spiffs)
            })
            console.log(allSpiffs)
            const map = new Map();

            for (const spiff of allSpiffs) {
                const userId = spiff.user_ID.id;

                if (!map.has(userId)) {
                map.set(userId, {
                    user_ID: spiff.user_ID,
                    spiffs: []
                });
                }

                map.get(userId).spiffs.push(spiff);
            }

            buildPDF(Array.from(map.values()))
        }

        processData(collected)
        } catch (err) {
         console.error("Failed fetching spiffees", err);
        }
    };

    return (
        <div style={{width: '360px', height: 'auto', display: 'flex', flexDirection: 'column'}}>
            <div style={{flexBasis: '10%', display: 'flex', flexDirection: 'row'}}>
                <span style={{fontWeight: '500', fontSize: '20px', flexBasis: '99%'}}>Export Prespiffs</span>
                <IconButton img={false} colors={props.colors} Style={{borderRadius: '3px', backgroundColor: props.colors.border, width: '24px'}} size="16px">
                    <div onClick={props.setIsOpen} style={{paddingLeft: '3px', paddingRight: '3px'}}><span onClick={props.setIsOpen}>x</span></div>
                </IconButton>
            </div>
            <div style={{ flexBasis: '90%', display: 'flex', flexDirection: 'column', marginTop: '20px' }}>
                <span onClick={() => console.log(allSpiffeeDetails)} style={{ fontWeight: '500', fontSize: '16px' }}>Downloaded Rows: {completedCount}</span>
                <Button colors={props.colors} active={true} Style={{ marginTop: '10px' }} onClick={() => downloadCSV()}>Download CSV</Button>
                <Button colors={props.colors} active={true} Style={{ marginTop: '10px' }} onClick={() => downloadPDF()}>Download PDF</Button>
            </div>
        </div>
    )
}

export default Main
